import { useEffect, useMemo, useState } from 'react';

import { bind, type UnbindFn } from 'bind-event-listener';

import { isFedrampModerate } from '@atlaskit/atlassian-context';

import AnalyticsCrossProductInteractionClient from './analyticsCrossProductInteractionClient';
import { generateUrlWithParams } from './generateUrlWithParams';
import { INTERACTION_SESSION_ID_UPDATED_EVENT } from './interactionSessionTracking';

/**
 * @param bridge - The name of your navigation component e.g. atlassianSwitcher
 * @param product - The product you are hosted on e.g. jira
 * @param subProduct - If specified, will be appended to the end of product as: product-subProduct
 */
export type CrossProductUrlOptions = {
	bridge: string;
	product: string;
	subProduct?: string;
};

/**
 * This React hook is called with the correct bridge, product and sub-product parameters.
 * It returns a function that can be used to generate URLs with cross-product interaction parameters.
 *
 * @param options - Properties object that contains the following parameters:
 *
 * @returns A function that appends interaction session ID and other cross-product interaction parameters to a given URL.
 */
export default function useCrossProductUrl(
	options: CrossProductUrlOptions,
): (url: string) => string {
	const [interactionSessionId, setInteractionSessionId] = useState<string>('');
	const { bridge, product, subProduct } = options;
	const isEnabled = !isFedrampModerate();

	// Client does not change on re-render - cache with useMemo
	const interactionSessionClient = useMemo(() => {
		if (isEnabled) {
			return new AnalyticsCrossProductInteractionClient();
		} else {
			return null;
		}
	}, [isEnabled]);

	useEffect(() => {
		if (interactionSessionClient && isEnabled) {
			// Fetch any initial interaction session ID
			const currentSessionId = interactionSessionClient.getCurrentInteractionSessionId();
			currentSessionId && setInteractionSessionId(currentSessionId);

			// Add event listener that subscribes to any future interaction session ID updates
			const unbind: UnbindFn = bind(document, {
				type: INTERACTION_SESSION_ID_UPDATED_EVENT,
				listener: () => {
					const currentSessionId = interactionSessionClient.getCurrentInteractionSessionId();
					currentSessionId && setInteractionSessionId(currentSessionId);
				},
			});

			return unbind;
		}
		return () => {};
	}, [interactionSessionClient, isEnabled]);

	if (!isEnabled) {
		return (url: string) => url;
	}

	return (url: string) =>
		generateUrlWithParams(url, bridge, interactionSessionId, product, subProduct);
}
